// @ts-nocheck
import { useState, createContext } from 'react';
import { api } from '@/utils/api';
import { useEventListener } from '@/hooks';
// @ts-ignore
import { enUSSiteWidgetLang as defaultLang } from '@concord-platform/platform-types/en_US';
import {
  ConfigContextType,
  ConsentEvent,
  ConsentState,
  StatusType,
  RequestType,
  ComplianceRequest,
  DefaultConfig,
  RegionTemplate,
} from '@/types';
import { computeForegroundColor } from '@/utils/utility';

function removeNulls(obj: RegionTemplate): RegionTemplate {
  if (Array.isArray(obj)) {
    return obj
      .filter((item) => item !== null && item !== undefined)
      .map(removeNulls);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, value]) => value !== null && value !== undefined)
        .map(([key, value]) => [key, removeNulls(value)]),
    );
  }
  return obj;
}

export const DEFAULT_CONFIG: DefaultConfig = {
  loaded: true,
  configLoaded: false,
  ready: false,
  logoUrl: null,
  poweredByConcord: true,
  enableFloatingButton: false,
  floatingButtonPosition: 'right',
  floatingButtonCircleOffsetX: '24px',
  floatingButtonCircleOffsetY: '24px',
  floatingButtonTabOffsetY: '256px',
  floatingButtonType: 'circle',
  floatingButtonTheme: 'brand',
  floatingButtonIcon: 'cookie',
  bannerLayout: 'bar',
  bannerTheme: 'light',
  brandTheme: 'dark',
  consentBlockingMode: 'disabled',
  consentMode: 'express',
  policyUrl: null,
  gcmMode: 'disabled',
  gcmEnabled: false,
  gcmPolicyUrl: 'https://business.safety.google/privacy',
  doNotSellConsentEnabled: false,
  enableGpc: false,
  limitPii: false,
  bannerEnabled: false,
  bannerPosition: 'bottom',
  bannerTitleEnabled: true,
  closeButtonEnabled: true,
  denyButtonEnabled: true,
  customizeButtonEnabled: true,
  consentHistoryEnabled: true,
  privacyRequestsEnabled: true,
  doNotSellRequestEnabled: false,
  privacyDisclosuresEnabled: true,
  privacyCenterPosition: 'right',
  privacyCenterTheme: 'brand',
  secondaryColor: '#030712',
};

export const DEFAULT_TEXT = {
  floatingButtonTabText: defaultLang.floatingButton.tab,
  bannerTitleText: defaultLang.consentBanner.title,
  bannerText: defaultLang.consentBanner.text,
  bannerLinkText: defaultLang.consentBanner.link,
  gcmLinkText: defaultLang.consentBanner.gcmLink,
  acceptButtonText: defaultLang.consentBanner.acceptButton,
  denyButtonText: defaultLang.consentBanner.denyButton,
  customizeButtonText: defaultLang.consentBanner.customizeButton,
  doNotSellText: defaultLang.consentBanner.doNotSellText,
  gpcText: defaultLang.consentBanner.gpcText,
  noItems: defaultLang.privacyCenter.noItems,
  errorText: defaultLang.privacyCenter.error.text,
  errorTitle: defaultLang.privacyCenter.error.title,
  privacyRequestsTitle: defaultLang.privacyCenter.privacyRequests.title,
  privacyRequestsDescription: defaultLang.privacyCenter.privacyRequests.text,
  privacyRequestsOptionsTitle:
    defaultLang.privacyCenter.privacyRequests.optionsTitle,
  privacyRequestsOptionsButtonText:
    defaultLang.privacyCenter.privacyRequests.optionsButton,
  privacyRequestsRequestsText:
    defaultLang.privacyCenter.privacyRequests.requests.text,
  privacyRequestsRequestsFirstName:
    defaultLang.privacyCenter.privacyRequests.requests.firstName,
  privacyRequestsRequestsLastName:
    defaultLang.privacyCenter.privacyRequests.requests.lastName,
  privacyRequestsRequestsEmail:
    defaultLang.privacyCenter.privacyRequests.requests.email,
  privacyRequestsRequestsEmailRequired:
    defaultLang.privacyCenter.privacyRequests.requests.emailRequired,
  privacyRequestsRequestsPhone:
    defaultLang.privacyCenter.privacyRequests.requests.phone,
  privacyRequestsRequestsPhoneRequired:
    defaultLang.privacyCenter.privacyRequests.requests.phoneRequired,
  privacyRequestsRequestsError:
    defaultLang.privacyCenter.privacyRequests.requests.error,
  privacyRequestsRequestsWarning:
    defaultLang.privacyCenter.privacyRequests.requests.warning,
  privacyRequestsGetDetailsButton:
    defaultLang.privacyCenter.privacyRequests.getDetailsButton,
  changeRequestName:
    defaultLang.privacyCenter.privacyRequests.changeRequest.title,
  changeRequestDescription:
    defaultLang.privacyCenter.privacyRequests.changeRequest.text,
  deleteRequestName:
    defaultLang.privacyCenter.privacyRequests.deleteRequest.title,
  deleteRequestDescription:
    defaultLang.privacyCenter.privacyRequests.deleteRequest.text,
  viewRequestName: defaultLang.privacyCenter.privacyRequests.viewRequest.title,
  viewRequestDescription:
    defaultLang.privacyCenter.privacyRequests.viewRequest.text,
  doNotSellRequestName:
    defaultLang.privacyCenter.privacyRequests.doNotSellRequest.title,
  doNotSellRequestDescription:
    defaultLang.privacyCenter.privacyRequests.doNotSellRequest.text,
  privacyDisclosuresName: defaultLang.privacyCenter.disclosures.title,
  privacyDisclosuresDescription: defaultLang.privacyCenter.disclosures.text,
  consentSettingsCancelButton:
    defaultLang.privacyCenter.consentSettings.cancelButton,
  consentSettingsSubmitButton:
    defaultLang.privacyCenter.consentSettings.submitButton,
  consentSettingsTitle: defaultLang.privacyCenter.consentSettings.title,
  consentSettingsText: defaultLang.privacyCenter.consentSettings.text,
  consentSettingsConfirmButton:
    defaultLang.privacyCenter.consentSettings.confirmButton,
  consentSettingsTabTitle: defaultLang.privacyCenter.consentSettings.tabTitle,
  consentSettingsAdditionalDetailsButton:
    defaultLang.privacyCenter.consentSettings.additionalDetailsButton,
  consentSettingsAlwaysActive:
    defaultLang.privacyCenter.consentSettings.alwaysActive,
  privacyCenterTabTitle: defaultLang.privacyCenter.tabTitle,
  consentHistoryTitle: defaultLang.privacyCenter.consentHistory.title,
  consentHistoryNoHistory: defaultLang.privacyCenter.consentHistory.noHistory,
  consentHistoryConsentEventsTitle:
    defaultLang.privacyCenter.consentHistory.consentEvents.title,
  consentHistoryConsentEventsName:
    defaultLang.privacyCenter.consentHistory.consentEvents.name,
  consentHistoryConsentEventsDescription:
    defaultLang.privacyCenter.consentHistory.consentEvents.description,
  consentHistoryConsentEventsDate:
    defaultLang.privacyCenter.consentHistory.consentEvents.date,
  consentHistoryConsentEventsDisclosure:
    defaultLang.privacyCenter.consentHistory.consentEvents.disclosure,
  consentHistoryConsentEventsLink:
    defaultLang.privacyCenter.consentHistory.consentEvents.link,
  consentHistoryStatesAccepted:
    defaultLang.privacyCenter.consentHistory.states.accepted,
  consentHistoryStatesDeclined:
    defaultLang.privacyCenter.consentHistory.states.declined,
  consentHistoryStatesImplied:
    defaultLang.privacyCenter.consentHistory.states.implied,
  consentCategoryStrictlyNecessaryTitle:
    defaultLang.privacyCenter.consentCategory.strictlyNecessary.title,
  consentCategoryStrictlyNecessaryText:
    defaultLang.privacyCenter.consentCategory.strictlyNecessary.text,
  consentCategoryAnalyticsTitle:
    defaultLang.privacyCenter.consentCategory.analytics.title,
  consentCategoryAnalyticsText:
    defaultLang.privacyCenter.consentCategory.analytics.text,
  consentCategoryFunctionalTitle:
    defaultLang.privacyCenter.consentCategory.functional.title,
  consentCategoryFunctionalText:
    defaultLang.privacyCenter.consentCategory.functional.text,
  consentCategoryMarketingTitle:
    defaultLang.privacyCenter.consentCategory.marketing.title,
  consentCategoryMarketingText:
    defaultLang.privacyCenter.consentCategory.marketing.text,
  consentCategoryUnclassifiedTitle:
    defaultLang.privacyCenter.consentCategory.unclassified.title,
  consentCategoryUnclassifiedText:
    defaultLang.privacyCenter.consentCategory.unclassified.text,
  consentCategoryLimitPiiUseTitle:
    defaultLang.privacyCenter.consentCategory.limitPiiUse.title,
  consentCategoryLimitPiiUseText:
    defaultLang.privacyCenter.consentCategory.limitPiiUse.text,
  consentCategoryDoNotSellTitle:
    defaultLang.privacyCenter.consentCategory.doNotSell.title,
  consentCategoryDoNotSellText:
    defaultLang.privacyCenter.consentCategory.doNotSell.text,
  consentCategoryGpcTitle: defaultLang.privacyCenter.consentCategory.gpc.title,
  consentCategoryGpcText: defaultLang.privacyCenter.consentCategory.gpc.text,
  consentCategoryPrivacyPolicyTitle:
    defaultLang.privacyCenter.consentCategory.privacyPolicy.title,
  consentCategoryTermsOfServiceTitle:
    defaultLang.privacyCenter.consentCategory.termsOfService.title,
  consentCategoryAgreementTitle:
    defaultLang.privacyCenter.consentCategory.agreement.title,
  consentCategoryDetailsName:
    defaultLang.privacyCenter.consentCategory.details.name,
  consentCategoryDetailsDomain:
    defaultLang.privacyCenter.consentCategory.details.domain,
  consentCategoryDetailsPattern:
    defaultLang.privacyCenter.consentCategory.details.pattern,
  consentSettingsUnidentifiedCompany:
    defaultLang.privacyCenter.consentSettings.unidentifiedCompany,
  privacyRequestsVerificationCodesTitle:
    defaultLang.privacyCenter.privacyRequests.verificationCodes.title,
  privacyRequestsVerificationCodesText:
    defaultLang.privacyCenter.privacyRequests.verificationCodes.text,
  privacyRequestsVerificationCodesPlaceholder:
    defaultLang.privacyCenter.privacyRequests.verificationCodes.placeholder,
  privacyRequestsVerificationCodesRequirements:
    defaultLang.privacyCenter.privacyRequests.verificationCodes.requirements,
  privacyRequestsVerificationCodesRequired:
    defaultLang.privacyCenter.privacyRequests.verificationCodes.required,
  privacyRequestsVerificationCodesInvalid:
    defaultLang.privacyCenter.privacyRequests.verificationCodes.invalid,
  privacyRequestsRequestDetailsTitle:
    defaultLang.privacyCenter.privacyRequests.requestDetails.title,
  privacyRequestsRequestDetailsText:
    defaultLang.privacyCenter.privacyRequests.requestDetails.text,
  privacyRequestsRequestDetailsRequired:
    defaultLang.privacyCenter.privacyRequests.requestDetails.required,
  privacyRequestStatusesSubmitted:
    defaultLang.privacyCenter.privacyRequests.statuses.submitted,
  privacyRequestStatusesPendingVerification:
    defaultLang.privacyCenter.privacyRequests.statuses.pendingVerification,
  privacyRequestStatusesResolved:
    defaultLang.privacyCenter.privacyRequests.statuses.resolved,
  privacyRequestStatusesAcknowledged:
    defaultLang.privacyCenter.privacyRequests.statuses.acknowledged,
  required: defaultLang.privacyCenter.required,
};

const defaultTitles: Record<RequestType, string> = {
  change: DEFAULT_TEXT.changeRequestName,
  delete: DEFAULT_TEXT.deleteRequestName,
  view: DEFAULT_TEXT.viewRequestName,
  do_not_sell: DEFAULT_TEXT.doNotSellRequestName,
};

const defaultRequestStatuses: Record<StatusType, string> = {
  acknowledged: DEFAULT_TEXT.privacyRequestStatusesAcknowledged,
  submitted: DEFAULT_TEXT.privacyRequestStatusesSubmitted,
  pending_verification: DEFAULT_TEXT.privacyRequestStatusesPendingVerification,
  resolved: DEFAULT_TEXT.privacyRequestStatusesResolved,
};

const defaultConsentStates: Record<ConsentState, string> = {
  accepted: DEFAULT_TEXT.consentHistoryStatesAccepted,
  declined: DEFAULT_TEXT.consentHistoryStatesDeclined,
  implied: DEFAULT_TEXT.consentHistoryStatesImplied,
};

const defaultConsentCategoryObj = {
  strictly_necessary: {
    title: DEFAULT_TEXT.consentCategoryStrictlyNecessaryTitle,
    text: DEFAULT_TEXT.consentCategoryStrictlyNecessaryText,
  },
  analytics: {
    title: DEFAULT_TEXT.consentCategoryAnalyticsTitle,
    text: DEFAULT_TEXT.consentCategoryAnalyticsText,
  },
  functional: {
    title: DEFAULT_TEXT.consentCategoryFunctionalTitle,
    text: DEFAULT_TEXT.consentCategoryFunctionalText,
  },
  marketing: {
    title: DEFAULT_TEXT.consentCategoryMarketingTitle,
    text: DEFAULT_TEXT.consentCategoryMarketingText,
  },
  unclassified: {
    title: DEFAULT_TEXT.consentCategoryUnclassifiedTitle,
    text: DEFAULT_TEXT.consentCategoryUnclassifiedText,
  },
  limit_pii_use: {
    title: DEFAULT_TEXT.consentCategoryLimitPiiUseTitle,
    text: DEFAULT_TEXT.consentCategoryLimitPiiUseText,
  },
  do_not_sell: {
    title: DEFAULT_TEXT.consentCategoryDoNotSellTitle,
    text: DEFAULT_TEXT.consentCategoryDoNotSellText,
  },
  global_privacy_control: {
    title: DEFAULT_TEXT.consentCategoryGpcTitle,
    text: DEFAULT_TEXT.consentCategoryGpcText,
  },
  privacy_policy: {
    title: DEFAULT_TEXT.consentCategoryPrivacyPolicyTitle,
  },
  terms_of_service: {
    title: DEFAULT_TEXT.consentCategoryTermsOfServiceTitle,
  },
  // agreement is not a category, but a placeholder for translation purposes
  agreement: {
    title: DEFAULT_TEXT.consentCategoryAgreementTitle,
  },
  details: {
    name: DEFAULT_TEXT.consentCategoryDetailsName,
    domain: DEFAULT_TEXT.consentCategoryDetailsDomain,
    pattern: DEFAULT_TEXT.consentCategoryDetailsPattern,
  },
};

export const ConfigContext = createContext<ConfigContextType>({
  ...DEFAULT_CONFIG,
  ...DEFAULT_TEXT,
  titles: { ...defaultTitles },
  requestStatuses: { ...defaultRequestStatuses },
  consentStates: { ...defaultConsentStates },
  consentCategories: { ...defaultConsentCategoryObj },
  getComplianceRequestTitle: (complianceRequest: ComplianceRequest) =>
    defaultTitles[complianceRequest.request as RequestType] || '',
  getComplianceRequestStatus: (complianceRequest: ComplianceRequest) =>
    defaultRequestStatuses[complianceRequest.status as StatusType] || '',
  getConsentEventStatus: (consentEvent: ConsentEvent) =>
    defaultConsentStates[consentEvent.consentState] || '',
});

export function useConfigContext() {
  const cleanedConfig = removeNulls(api.config?.regionTemplate ?? {});

  const {
    settings: {
      branding: {
        logoUrl = DEFAULT_CONFIG.logoUrl,
        poweredByConcord = DEFAULT_CONFIG.poweredByConcord,
        secondaryColor = DEFAULT_CONFIG.secondaryColor,
      } = {},
      consent: {
        blockingMode: consentBlockingMode = DEFAULT_CONFIG.consentBlockingMode,
        consentMode = DEFAULT_CONFIG.consentMode,
        consentBanner: {
          closeButton: closeButtonEnabled = DEFAULT_CONFIG.closeButtonEnabled,
          customizeButton:
            customizeButtonEnabled = DEFAULT_CONFIG.customizeButtonEnabled,
          denyButton: denyButtonEnabled = DEFAULT_CONFIG.denyButtonEnabled,
          enabled: bannerEnabled = DEFAULT_CONFIG.bannerEnabled,
          layout: bannerLayout = DEFAULT_CONFIG.bannerLayout,
          policy: { url: policyUrl = null } = {},
          position: bannerPosition = DEFAULT_CONFIG.bannerPosition,
          theme: bannerTheme = DEFAULT_CONFIG.bannerTheme,
          title: bannerTitleEnabled = DEFAULT_CONFIG.bannerTitleEnabled,
        } = {},
        doNotSell:
          doNotSellConsentEnabled = DEFAULT_CONFIG.doNotSellConsentEnabled,
        gcm: gcmMode = DEFAULT_CONFIG.gcmMode,
        gcmPolicy: gcmPolicyUrl = DEFAULT_CONFIG.gcmPolicyUrl,
        gpc: enableGpc = DEFAULT_CONFIG.enableGpc,
        limitPii = DEFAULT_CONFIG.limitPii,
      } = {},
      privacyCenter: {
        consentHistory:
          consentHistoryEnabled = DEFAULT_CONFIG.consentHistoryEnabled,
        disclosures:
          privacyDisclosuresEnabled = DEFAULT_CONFIG.privacyDisclosuresEnabled,
        privacyRequests:
          privacyRequestsEnabled = DEFAULT_CONFIG.privacyRequestsEnabled,
        doNotSellRequests:
          doNotSellRequestEnabled = DEFAULT_CONFIG.doNotSellRequestEnabled,
        position: privacyCenterPosition = DEFAULT_CONFIG.privacyCenterPosition,
        theme: privacyCenterTheme = DEFAULT_CONFIG.privacyCenterTheme,
      } = {},
      floatingButton: {
        enabled: enableFloatingButton = DEFAULT_CONFIG.enableFloatingButton,
        position:
          floatingButtonPosition = DEFAULT_CONFIG.floatingButtonPosition,
        circleOffsetX:
          floatingButtonCircleOffsetX = DEFAULT_CONFIG.floatingButtonCircleOffsetX,
        circleOffsetY:
          floatingButtonCircleOffsetY = DEFAULT_CONFIG.floatingButtonCircleOffsetY,
        tabOffsetY:
          floatingButtonTabOffsetY = DEFAULT_CONFIG.floatingButtonTabOffsetY,
        theme: floatingButtonTheme = DEFAULT_CONFIG.floatingButtonTheme,
        type: floatingButtonType = DEFAULT_CONFIG.floatingButtonType,
        icon: floatingButtonIcon = DEFAULT_CONFIG.floatingButtonIcon,
      } = {},
    } = {},
  } = cleanedConfig;

  const languageConfig = removeNulls(api?.config?.language ?? {});

  const {
    'consentBanner.title': bannerTitleText = DEFAULT_TEXT.bannerTitleText,
    'consentBanner.text': bannerText = DEFAULT_TEXT.bannerText,
    'consentBanner.link': bannerLinkText = DEFAULT_TEXT.bannerLinkText,
    'consentBanner.gcmLink': gcmLinkText = DEFAULT_TEXT.gcmLinkText,
    'consentBanner.acceptButton':
      acceptButtonText = DEFAULT_TEXT.acceptButtonText,
    'consentBanner.denyButton': denyButtonText = DEFAULT_TEXT.denyButtonText,
    'consentBanner.customizeButton':
      customizeButtonText = DEFAULT_TEXT.customizeButtonText,
    'consentBanner.doNotSellText': doNotSellText = DEFAULT_TEXT.doNotSellText,
    'consentBanner.gpcText': gpcText = DEFAULT_TEXT.gpcText,
    'floatingButton.tab':
      floatingButtonTabText = DEFAULT_TEXT.floatingButtonTabText,
    'privacyCenter.error.title': errorTitle = DEFAULT_TEXT.errorTitle,
    'privacyCenter.error.text': errorText = DEFAULT_TEXT.errorText,
    'privacyCenter.noItems': noItems = DEFAULT_TEXT.noItems,
    'privacyCenter.consentHistory.title':
      consentHistoryTitle = DEFAULT_TEXT.consentHistoryTitle,
    'privacyCenter.consentHistory.noHistory':
      consentHistoryNoHistory = DEFAULT_TEXT.consentHistoryNoHistory,
    'privacyCenter.consentHistory.consentEvents.title':
      consentHistoryConsentEventsTitle = DEFAULT_TEXT.consentHistoryConsentEventsTitle,
    'privacyCenter.consentHistory.consentEvents.name':
      consentHistoryConsentEventsName = DEFAULT_TEXT.consentHistoryConsentEventsName,
    'privacyCenter.consentHistory.consentEvents.description':
      consentHistoryConsentEventsDescription = DEFAULT_TEXT.consentHistoryConsentEventsDescription,
    'privacyCenter.consentHistory.consentEvents.date':
      consentHistoryConsentEventsDate = DEFAULT_TEXT.consentHistoryConsentEventsDate,
    'privacyCenter.consentHistory.consentEvents.disclosure':
      consentHistoryConsentEventsDisclosure = DEFAULT_TEXT.consentHistoryConsentEventsDisclosure,
    'privacyCenter.consentHistory.consentEvents.link':
      consentHistoryConsentEventsLink = DEFAULT_TEXT.consentHistoryConsentEventsLink,
    'privacyCenter.consentHistory.states.accepted':
      consentHistoryStatesAccepted = DEFAULT_TEXT.consentHistoryStatesAccepted,
    'privacyCenter.consentHistory.states.declined':
      consentHistoryStatesDeclined = DEFAULT_TEXT.consentHistoryStatesDeclined,
    'privacyCenter.consentHistory.states.implied':
      consentHistoryStatesImplied = DEFAULT_TEXT.consentHistoryStatesImplied,
    'privacyCenter.disclosures.title':
      privacyDisclosuresName = DEFAULT_TEXT.privacyDisclosuresName,
    'privacyCenter.disclosures.text':
      privacyDisclosuresDescription = DEFAULT_TEXT.privacyDisclosuresDescription,
    'privacyCenter.privacyRequests.title':
      privacyRequestsTitle = DEFAULT_TEXT.privacyRequestsTitle,
    'privacyCenter.privacyRequests.text':
      privacyRequestsDescription = DEFAULT_TEXT.privacyRequestsDescription,
    'privacyCenter.privacyRequests.optionsTitle':
      privacyRequestsOptionsTitle = DEFAULT_TEXT.privacyRequestsOptionsTitle,
    'privacyCenter.privacyRequests.optionsButton':
      privacyRequestsOptionsButtonText = DEFAULT_TEXT.privacyRequestsOptionsButtonText,
    'privacyCenter.privacyRequests.changeRequest.title':
      changeRequestName = DEFAULT_TEXT.changeRequestName,
    'privacyCenter.privacyRequests.changeRequest.text':
      changeRequestDescription = DEFAULT_TEXT.changeRequestDescription,
    'privacyCenter.privacyRequests.deleteRequest.title':
      deleteRequestName = DEFAULT_TEXT.deleteRequestName,
    'privacyCenter.privacyRequests.deleteRequest.text':
      deleteRequestDescription = DEFAULT_TEXT.deleteRequestDescription,
    'privacyCenter.privacyRequests.doNotSellRequest.title':
      doNotSellRequestName = DEFAULT_TEXT.doNotSellRequestName,
    'privacyCenter.privacyRequests.doNotSellRequest.text':
      doNotSellRequestDescription = DEFAULT_TEXT.doNotSellRequestDescription,
    'privacyCenter.privacyRequests.viewRequest.title':
      viewRequestName = DEFAULT_TEXT.viewRequestName,
    'privacyCenter.privacyRequests.viewRequest.text':
      viewRequestDescription = DEFAULT_TEXT.viewRequestDescription,
    'privacyCenter.consentSettings.additionalDetailsButton':
      consentSettingsAdditionalDetailsButton = DEFAULT_TEXT.consentSettingsAdditionalDetailsButton,
    'privacyCenter.consentSettings.alwaysActive':
      consentSettingsAlwaysActive = DEFAULT_TEXT.consentSettingsAlwaysActive,
    'privacyCenter.consentSettings.title':
      consentSettingsTitle = DEFAULT_TEXT.consentSettingsTitle,
    'privacyCenter.consentSettings.text':
      consentSettingsText = DEFAULT_TEXT.consentSettingsText,
    'privacyCenter.consentSettings.confirmButton':
      consentSettingsConfirmButton = DEFAULT_TEXT.consentSettingsConfirmButton,
    'privacyCenter.consentSettings.tabTitle':
      consentSettingsTabTitle = DEFAULT_TEXT.consentSettingsTabTitle,
    'privacyCenter.tabTitle':
      privacyCenterTabTitle = DEFAULT_TEXT.privacyCenterTabTitle,
    'privacyCenter.consentCategory.strictlyNecessary.title':
      consentCategoryStrictlyNecessaryTitle = DEFAULT_TEXT.consentCategoryStrictlyNecessaryTitle,
    'privacyCenter.consentCategory.strictlyNecessary.text':
      consentCategoryStrictlyNecessaryText = DEFAULT_TEXT.consentCategoryStrictlyNecessaryText,
    'privacyCenter.consentCategory.analytics.title':
      consentCategoryAnalyticsTitle = DEFAULT_TEXT.consentCategoryAnalyticsTitle,
    'privacyCenter.consentCategory.analytics.text':
      consentCategoryAnalyticsText = DEFAULT_TEXT.consentCategoryAnalyticsText,
    'privacyCenter.consentCategory.functional.title':
      consentCategoryFunctionalTitle = DEFAULT_TEXT.consentCategoryFunctionalTitle,
    'privacyCenter.consentCategory.functional.text':
      consentCategoryFunctionalText = DEFAULT_TEXT.consentCategoryFunctionalText,
    'privacyCenter.consentCategory.marketing.title':
      consentCategoryMarketingTitle = DEFAULT_TEXT.consentCategoryMarketingTitle,
    'privacyCenter.consentCategory.marketing.text':
      consentCategoryMarketingText = DEFAULT_TEXT.consentCategoryMarketingText,
    'privacyCenter.consentCategory.unclassified.title':
      consentCategoryUnclassifiedTitle = DEFAULT_TEXT.consentCategoryUnclassifiedTitle,
    'privacyCenter.consentCategory.unclassified.text':
      consentCategoryUnclassifiedText = DEFAULT_TEXT.consentCategoryUnclassifiedText,
    'privacyCenter.consentCategory.limitPiiUse.title':
      consentCategoryLimitPiiUseTitle = DEFAULT_TEXT.consentCategoryLimitPiiUseTitle,
    'privacyCenter.consentCategory.limitPiiUse.text':
      consentCategoryLimitPiiUseText = DEFAULT_TEXT.consentCategoryLimitPiiUseText,
    'privacyCenter.consentCategory.doNotSell.title':
      consentCategoryDoNotSellTitle = DEFAULT_TEXT.consentCategoryDoNotSellTitle,
    'privacyCenter.consentCategory.doNotSell.text':
      consentCategoryDoNotSellText = DEFAULT_TEXT.consentCategoryDoNotSellText,
    'privacyCenter.consentCategory.gpc.title':
      consentCategoryGpcTitle = DEFAULT_TEXT.consentCategoryGpcTitle,
    'privacyCenter.consentCategory.gpc.text':
      consentCategoryGpcText = DEFAULT_TEXT.consentCategoryGpcText,
    'privacyCenter.consentCategory.privacyPolicy.title':
      consentCategoryPrivacyPolicyTitle = DEFAULT_TEXT.consentCategoryPrivacyPolicyTitle,
    'privacyCenter.consentCategory.termsOfService.title':
      consentCategoryTermsOfServiceTitle = DEFAULT_TEXT.consentCategoryTermsOfServiceTitle,
    'privacyCenter.consentCategory.agreement.title':
      consentCategoryAgreementTitle = DEFAULT_TEXT.consentCategoryAgreementTitle,
    'privacyCenter.consentCategory.details.name':
      consentCategoryDetailsName = DEFAULT_TEXT.consentCategoryDetailsName,
    'privacyCenter.consentCategory.details.domain':
      consentCategoryDetailsDomain = DEFAULT_TEXT.consentCategoryDetailsDomain,
    'privacyCenter.consentCategory.details.pattern':
      consentCategoryDetailsPattern = DEFAULT_TEXT.consentCategoryDetailsPattern,
    'privacyCenter.consentSettings.unidentifiedCompany':
      consentSettingsUnidentifiedCompany = DEFAULT_TEXT.consentSettingsUnidentifiedCompany,
    'privacyCenter.consentSettings.cancelButton':
      consentSettingsCancelButton = DEFAULT_TEXT.consentSettingsCancelButton,
    'privacyCenter.consentSettings.submitButton':
      consentSettingsSubmitButton = DEFAULT_TEXT.consentSettingsSubmitButton,
    'privacyCenter.privacyRequests.requests.text':
      privacyRequestsRequestsText = DEFAULT_TEXT.privacyRequestsRequestsText,
    'privacyCenter.privacyRequests.requests.firstName':
      privacyRequestsRequestsFirstName = DEFAULT_TEXT.privacyRequestsRequestsFirstName,
    'privacyCenter.privacyRequests.requests.lastName':
      privacyRequestsRequestsLastName = DEFAULT_TEXT.privacyRequestsRequestsLastName,
    'privacyCenter.privacyRequests.requests.email':
      privacyRequestsRequestsEmail = DEFAULT_TEXT.privacyRequestsRequestsEmail,
    'privacyCenter.privacyRequests.requests.emailRequired':
      privacyRequestsRequestsEmailRequired = DEFAULT_TEXT.privacyRequestsRequestsEmailRequired,
    'privacyCenter.privacyRequests.requests.phone':
      privacyRequestsRequestsPhone = DEFAULT_TEXT.privacyRequestsRequestsPhone,
    'privacyCenter.privacyRequests.requests.phoneRequired':
      privacyRequestsRequestsPhoneRequired = DEFAULT_TEXT.privacyRequestsRequestsPhoneRequired,
    'privacyCenter.privacyRequests.requests.error':
      privacyRequestsRequestsError = DEFAULT_TEXT.privacyRequestsRequestsError,
    'privacyCenter.privacyRequests.requests.warning':
      privacyRequestsRequestsWarning = DEFAULT_TEXT.privacyRequestsRequestsWarning,
    'privacyCenter.privacyRequests.getDetailsButton':
      privacyRequestsGetDetailsButton = DEFAULT_TEXT.privacyRequestsGetDetailsButton,
    'privacyCenter.privacyRequests.verificationCodes.text':
      privacyRequestsVerificationCodesText = DEFAULT_TEXT.privacyRequestsVerificationCodesText,
    'privacyCenter.privacyRequests.verificationCodes.placeholder':
      privacyRequestsVerificationCodesPlaceholder = DEFAULT_TEXT.privacyRequestsVerificationCodesPlaceholder,
    'privacyCenter.privacyRequests.verificationCodes.title':
      privacyRequestsVerificationCodesTitle = DEFAULT_TEXT.privacyRequestsVerificationCodesTitle,
    'privacyCenter.privacyRequests.verificationCodes.requirements':
      privacyRequestsVerificationCodesRequirements = DEFAULT_TEXT.privacyRequestsVerificationCodesRequirements,
    'privacyCenter.privacyRequests.verificationCodes.required':
      privacyRequestsVerificationCodesRequired = DEFAULT_TEXT.privacyRequestsVerificationCodesRequired,
    'privacyCenter.privacyRequests.verificationCodes.invalid':
      privacyRequestsVerificationCodesInvalid = DEFAULT_TEXT.privacyRequestsVerificationCodesInvalid,
    'privacyCenter.privacyRequests.requestDetails.title':
      privacyRequestsRequestDetailsTitle = DEFAULT_TEXT.privacyRequestsRequestDetailsTitle,
    'privacyCenter.privacyRequests.requestDetails.text':
      privacyRequestsRequestDetailsText = DEFAULT_TEXT.privacyRequestsRequestDetailsText,
    'privacyCenter.privacyRequests.requestDetails.required':
      privacyRequestsRequestDetailsRequired = DEFAULT_TEXT.privacyRequestsRequestDetailsRequired,
    'privacyCenter.privacyRequests.statuses.submitted':
      privacyRequestStatusesSubmitted = DEFAULT_TEXT.privacyRequestStatusesSubmitted,
    'privacyCenter.privacyRequests.statuses.pendingVerification':
      privacyRequestStatusesPendingVerification = DEFAULT_TEXT.privacyRequestStatusesPendingVerification,
    'privacyCenter.privacyRequests.statuses.resolved':
      privacyRequestStatusesResolved = DEFAULT_TEXT.privacyRequestStatusesResolved,
    'privacyCenter.privacyRequests.statuses.acknowledged':
      privacyRequestStatusesAcknowledged = DEFAULT_TEXT.privacyRequestStatusesAcknowledged,
    'privacyCenter.required': required = DEFAULT_TEXT.required,
  } = languageConfig;

  const titles = {
    change: changeRequestName || DEFAULT_TEXT.changeRequestName,
    delete: deleteRequestName || DEFAULT_TEXT.deleteRequestName,
    view: viewRequestName || DEFAULT_TEXT.viewRequestName,
    do_not_sell: doNotSellRequestName || DEFAULT_TEXT.doNotSellRequestName,
  };

  const requestStatuses = {
    acknowledged:
      privacyRequestStatusesAcknowledged ||
      DEFAULT_TEXT.privacyRequestStatusesAcknowledged,
    submitted:
      privacyRequestStatusesSubmitted ||
      DEFAULT_TEXT.privacyRequestStatusesSubmitted,
    pending_verification:
      privacyRequestStatusesPendingVerification ||
      DEFAULT_TEXT.privacyRequestStatusesPendingVerification,
    resolved:
      privacyRequestStatusesResolved ||
      DEFAULT_TEXT.privacyRequestStatusesResolved,
  };

  const consentStates = {
    accepted:
      consentHistoryStatesAccepted || DEFAULT_TEXT.consentHistoryStatesAccepted,
    declined:
      consentHistoryStatesDeclined || DEFAULT_TEXT.consentHistoryStatesDeclined,
    implied:
      consentHistoryStatesImplied || DEFAULT_TEXT.consentHistoryStatesImplied,
  };

  const consentCategories = {
    strictly_necessary: {
      title:
        consentCategoryStrictlyNecessaryTitle ||
        DEFAULT_TEXT.consentCategoryStrictlyNecessaryTitle,
      text:
        consentCategoryStrictlyNecessaryText ||
        DEFAULT_TEXT.consentCategoryStrictlyNecessaryText,
    },
    analytics: {
      title:
        consentCategoryAnalyticsTitle ||
        DEFAULT_TEXT.consentCategoryAnalyticsTitle,
      text:
        consentCategoryAnalyticsText ||
        DEFAULT_TEXT.consentCategoryAnalyticsText,
    },
    functional: {
      title:
        consentCategoryFunctionalTitle ||
        DEFAULT_TEXT.consentCategoryFunctionalTitle,
      text:
        consentCategoryFunctionalText ||
        DEFAULT_TEXT.consentCategoryFunctionalText,
    },
    marketing: {
      title:
        consentCategoryMarketingTitle ||
        DEFAULT_TEXT.consentCategoryMarketingTitle,
      text:
        consentCategoryMarketingText ||
        DEFAULT_TEXT.consentCategoryMarketingText,
    },
    unclassified: {
      title:
        consentCategoryUnclassifiedTitle ||
        DEFAULT_TEXT.consentCategoryUnclassifiedTitle,
      text:
        consentCategoryUnclassifiedText ||
        DEFAULT_TEXT.consentCategoryUnclassifiedText,
    },
    limit_pii_use: {
      title:
        consentCategoryLimitPiiUseTitle ||
        DEFAULT_TEXT.consentCategoryLimitPiiUseTitle,
      text:
        consentCategoryLimitPiiUseText ||
        DEFAULT_TEXT.consentCategoryLimitPiiUseText,
    },
    do_not_sell: {
      title:
        consentCategoryDoNotSellTitle ||
        DEFAULT_TEXT.consentCategoryDoNotSellTitle,
      text:
        consentCategoryDoNotSellText ||
        DEFAULT_TEXT.consentCategoryDoNotSellText,
    },
    global_privacy_control: {
      title: consentCategoryGpcTitle || DEFAULT_TEXT.consentCategoryGpcTitle,
      text: consentCategoryGpcText || DEFAULT_TEXT.consentCategoryGpcText,
    },
    privacy_policy: {
      title:
        consentCategoryPrivacyPolicyTitle ||
        DEFAULT_TEXT.consentCategoryPrivacyPolicyTitle,
    },
    terms_of_service: {
      title:
        consentCategoryTermsOfServiceTitle ||
        DEFAULT_TEXT.consentCategoryTermsOfServiceTitle,
    },
    // agreement is not a category, but a placeholder for translation purposes
    agreement: {
      title:
        consentCategoryAgreementTitle ||
        DEFAULT_TEXT.consentCategoryAgreementTitle,
    },
    details: {
      name:
        consentCategoryDetailsName || DEFAULT_TEXT.consentCategoryDetailsName,
      domain:
        consentCategoryDetailsDomain ||
        DEFAULT_TEXT.consentCategoryDetailsDomain,
      pattern:
        consentCategoryDetailsPattern ||
        DEFAULT_TEXT.consentCategoryDetailsPattern,
    },
  };

  function getObjectValue<T>(
    obj: Record<string, unknown>,
    property: string,
    valueMap: Record<string, T>,
    defaultValue: T,
  ): T {
    if (
      typeof obj[property] === 'string' &&
      (obj[property] as string) in valueMap
    ) {
      return valueMap[obj[property] as string];
    }
    return defaultValue;
  }

  function getComplianceRequestTitle(complianceRequest: ComplianceRequest) {
    return getObjectValue(
      complianceRequest,
      'request',
      titles,
      'Privacy Request',
    );
  }

  function getComplianceRequestStatus(complianceRequest: ComplianceRequest) {
    return getObjectValue(
      complianceRequest,
      'status',
      requestStatuses,
      DEFAULT_TEXT.privacyRequestStatusesPendingVerification,
    );
  }

  function getConsentEventStatus(consentEvent: ConsentEvent) {
    return getObjectValue(consentEvent, 'consentState', consentStates, '');
  }

  const [context, setContext] = useState({
    ...DEFAULT_CONFIG,
    ...DEFAULT_TEXT,
    configLoaded: !!api?.config,
    config: api?.config || undefined,
    ready: api?.ready,
    logoUrl,
    poweredByConcord,
    enableFloatingButton,
    floatingButtonPosition,
    floatingButtonCircleOffsetX,
    floatingButtonCircleOffsetY,
    floatingButtonTabOffsetY,
    floatingButtonType,
    floatingButtonIcon,
    floatingButtonTheme,
    floatingButtonTabText,
    bannerTheme,
    brandTheme:
      computeForegroundColor(secondaryColor) === '#fff' ? 'dark' : 'light',
    consentBlockingMode,
    consentMode,
    doNotSellConsentEnabled,
    enableGpc,
    limitPii,
    bannerEnabled,
    bannerLayout,
    bannerPosition,
    bannerTitleEnabled,
    bannerTitleText,
    bannerText,
    policyUrl,
    bannerLinkText,
    gcmMode,
    gcmEnabled: gcmMode === 'basic' || gcmMode === 'advanced',
    gcmPolicyUrl,
    gcmLinkText,
    closeButtonEnabled,
    acceptButtonText,
    denyButtonEnabled,
    denyButtonText,
    doNotSellText,
    gpcText,
    customizeButtonEnabled,
    customizeButtonText,
    privacyCenterPosition,
    privacyCenterTheme,
    consentHistoryEnabled,
    consentHistoryTitle,
    consentHistoryNoHistory,
    consentHistoryStatesAccepted,
    consentHistoryStatesDeclined,
    consentHistoryStatesImplied,
    consentHistoryConsentEventsTitle,
    consentHistoryConsentEventsName,
    consentHistoryConsentEventsDescription,
    consentHistoryConsentEventsDate,
    consentHistoryConsentEventsDisclosure,
    consentHistoryConsentEventsLink,
    privacyRequestsEnabled,
    privacyRequestsTitle,
    privacyRequestsDescription,
    privacyRequestsOptionsTitle,
    privacyRequestsOptionsButtonText,
    changeRequestName,
    changeRequestDescription,
    deleteRequestName,
    deleteRequestDescription,
    viewRequestName,
    viewRequestDescription,
    doNotSellRequestEnabled,
    doNotSellRequestName,
    doNotSellRequestDescription,
    privacyDisclosuresEnabled,
    privacyDisclosuresName,
    privacyDisclosuresDescription,
    consentSettingsAdditionalDetailsButton,
    consentSettingsAlwaysActive,
    consentSettingsTitle,
    consentSettingsText,
    consentSettingsConfirmButton,
    consentSettingsTabTitle,
    privacyCenterTabTitle,
    consentCategoryStrictlyNecessaryTitle,
    consentCategoryStrictlyNecessaryText,
    consentCategoryAnalyticsTitle,
    consentCategoryAnalyticsText,
    consentCategoryFunctionalTitle,
    consentCategoryFunctionalText,
    consentCategoryMarketingTitle,
    consentCategoryMarketingText,
    consentCategoryUnclassifiedTitle,
    consentCategoryUnclassifiedText,
    consentCategoryLimitPiiUseTitle,
    consentCategoryLimitPiiUseText,
    consentCategoryDoNotSellTitle,
    consentCategoryDoNotSellText,
    consentCategoryGpcTitle,
    consentCategoryGpcText,
    consentCategoryPrivacyPolicyTitle,
    consentCategoryTermsOfServiceTitle,
    consentCategoryAgreementTitle,
    consentCategoryDetailsName,
    consentCategoryDetailsDomain,
    consentCategoryDetailsPattern,
    consentSettingsUnidentifiedCompany,
    errorText,
    errorTitle,
    noItems,
    privacyRequestsRequestsText,
    privacyRequestsRequestsFirstName,
    privacyRequestsRequestsLastName,
    privacyRequestsRequestsEmail,
    privacyRequestsRequestsEmailRequired,
    privacyRequestsRequestsPhone,
    privacyRequestsRequestsPhoneRequired,
    privacyRequestsRequestsError,
    privacyRequestsRequestsWarning,
    consentSettingsCancelButton,
    consentSettingsSubmitButton,
    privacyRequestsGetDetailsButton,
    privacyRequestsVerificationCodesPlaceholder,
    privacyRequestsVerificationCodesText,
    privacyRequestsVerificationCodesTitle,
    privacyRequestsVerificationCodesRequirements,
    privacyRequestsVerificationCodesRequired,
    privacyRequestsVerificationCodesInvalid,
    privacyRequestsRequestDetailsTitle,
    privacyRequestsRequestDetailsText,
    privacyRequestsRequestDetailsRequired,
    privacyRequestStatusesSubmitted,
    privacyRequestStatusesPendingVerification,
    privacyRequestStatusesResolved,
    privacyRequestStatusesAcknowledged,
    required,
  });

  useEventListener(api.events.Ready, () => {
    setContext({ ...context, ready: true });
  });

  return {
    ...context,
    titles,
    requestStatuses,
    consentStates,
    consentCategories,
    getComplianceRequestTitle,
    getComplianceRequestStatus,
    getConsentEventStatus,
  };
}
